import { motion } from "framer-motion";
import {
  Sparkles, Gavel, AlertTriangle, BookMarked, Repeat, TrendingUp, Coins, ArrowDownToLine,
} from "lucide-react";
import { cls } from "../../lib/cls";
import type { ScenarioStep, ScenarioKind } from "../../lib/scenario";

/* Apple-style scenario preview — vertical timeline (CSS only).
 *
 * Replaced the xyflow build: the auto-fit was zooming out hard on
 * wide cockpit panels and parking the nodes on one side. The flow is
 * a strict top-to-bottom sequence — a real graph engine is overkill
 * for a linear DAG. CSS gives us a perfect fill with zero clipping,
 * works at every viewport width, and respects prefers-reduced-motion.
 */

const KIND_GLYPH: Record<ScenarioKind, JSX.Element> = {
  ingest:        <ArrowDownToLine className="w-3.5 h-3.5" />,
  ruleFire:      <Gavel           className="w-3.5 h-3.5" />,
  alert:         <AlertTriangle   className="w-3.5 h-3.5" />,
  memoryMatch:   <BookMarked      className="w-3.5 h-3.5" />,
  substitute:    <Repeat          className="w-3.5 h-3.5" />,
  explain:       <Sparkles        className="w-3.5 h-3.5" />,
  budgetSlip:    <Coins           className="w-3.5 h-3.5" />,
  styleDrift:    <Sparkles        className="w-3.5 h-3.5" />,
  approvalAged:  <AlertTriangle   className="w-3.5 h-3.5" />,
  memoryPromote: <TrendingUp      className="w-3.5 h-3.5" />,
};

/* Visual language per scenario step kind. Alert steps use the SAME
 * foyr-orange as the hero "N alerts" badge so the cockpit reads as
 * one consistent surface — an alert is an alert. Rule fires use sky-
 * blue so they're clearly different (a rule firing isn't always an
 * alert). */
const KIND_RING: Record<ScenarioKind, string> = {
  ingest:        "ring-ink-300    text-ink-700      bg-cream-dark",
  ruleFire:      "ring-sky-300    text-sky-700      bg-sky-50/40",
  alert:         "ring-foyr/40    text-foyr-deep    bg-foyr/5",
  memoryMatch:   "ring-amber-300  text-amber-700    bg-amber-50/40",
  substitute:    "ring-fern/40    text-fern         bg-fern/5",
  explain:       "ring-violet-300 text-violet-700   bg-violet-50/40",
  budgetSlip:    "ring-foyr/40    text-foyr-deep    bg-foyr/5",
  styleDrift:    "ring-fuchsia-300 text-fuchsia-700 bg-fuchsia-50/40",
  approvalAged:  "ring-foyr/40    text-foyr-deep    bg-foyr/5",
  memoryPromote: "ring-fern/40    text-fern         bg-fern/5",
};

/* Per-step-kind badge tone — chosen so an "alert" step in the
 * scenario matches the orange "N alerts" hero badge. Rule fires are
 * sky-blue to read as "rule" (not "alert"). Substitute = fern green,
 * money = foyr-deep. */
function badgeTone(kind: ScenarioKind, badge?: string): string {
  if (kind === "alert" || kind === "approvalAged" || kind === "budgetSlip") {
    return "bg-foyr/10 text-foyr-deep border-foyr/30";
  }
  if (kind === "ruleFire") {
    return "bg-sky-50 text-sky-700 border-sky-200";
  }
  if (kind === "substitute" || kind === "memoryPromote") {
    return "bg-fern/10 text-fern border-fern/30";
  }
  if (kind === "memoryMatch") {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }
  // Fall back to legacy badge field for ingest / explain / styleDrift.
  if (badge === "money") return "bg-ink-950 text-cream border-ink-950";
  if (badge === "ok")    return "bg-fern/10 text-fern border-fern/30";
  if (badge === "warn")  return "bg-foyr/10 text-foyr-deep border-foyr/30";
  return "bg-cream-dark text-ink-600 border-border";
}

interface ScenarioPreviewGraphProps {
  steps: readonly ScenarioStep[];
  /** Index of the currently-playing step, or -1 for paused / idle. */
  activeIndex?: number;
  /** When true, the steps came from the fallback `selected.steps` array
   *  (generator silent). Dim everything so the user can see it's not live. */
  fallback?: boolean;
}

export function ScenarioPreviewGraph({
  steps, activeIndex = -1, fallback = false,
}: ScenarioPreviewGraphProps): JSX.Element {
  return (
    <div className={cls(
      "rounded-2xl border border-border/60 bg-gradient-to-br from-cream via-cream to-cream-dark/30 p-4 sm:p-5",
      fallback && "opacity-70",
    )}>
      <ol className="relative space-y-3">
        {/* The vertical connector line — dotted, soft. */}
        <div
          className="absolute left-[19px] top-3 bottom-3 w-px border-l-2 border-dotted border-ink-300/60 pointer-events-none"
          aria-hidden
        />
        {steps.map((s, i) => {
          const active = i === activeIndex;
          return (
            <motion.li
              key={s.id || `step-${i}`}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.22, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
              className="relative flex items-start gap-3"
            >
              {/* Step glyph + number — sits on the connector line. */}
              <div className={cls(
                "relative z-10 grid place-items-center w-10 h-10 rounded-xl shrink-0 ring-1 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-shadow",
                active
                  ? "!ring-2 !ring-foyr bg-foyr text-cream shadow-[0_4px_14px_-2px_rgba(201,92,26,0.30)]"
                  : KIND_RING[s.kind],
              )}>
                {KIND_GLYPH[s.kind]}
              </div>

              {/* The card itself. */}
              <div className={cls(
                "flex-1 min-w-0 rounded-xl bg-cream border px-3.5 py-2.5 transition-shadow",
                active
                  ? "border-foyr/40 shadow-[0_4px_14px_-2px_rgba(201,92,26,0.15)]"
                  : "border-border/70 shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06)]",
              )}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-newyork text-[15px] font-semibold text-ink-950 leading-tight tracking-tight">
                    {s.label}
                  </div>
                  <span className="shrink-0 grid place-items-center w-5 h-5 rounded-full bg-cream-dark text-[10px] font-mono tabular text-ink-700">
                    {i + 1}
                  </span>
                </div>
                <div className="text-[12px] text-ink-600 leading-snug mt-1">{s.detail}</div>
                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                  {s.badge ? (
                    <span className={cls("pill text-[9.5px] border", badgeTone(s.kind, s.badge))}>{s.badge}</span>
                  ) : null}
                  {s.delta ? (
                    <span className="pill text-[9.5px] border border-foyr/30 bg-foyr/10 text-foyr-deep">{s.delta}</span>
                  ) : null}
                  {s.highlightNodes.length > 0 ? (
                    <span className="text-[9.5px] text-ink-400 font-mono truncate" title={`Cites: ${s.highlightNodes.join(", ")}`}>
                      cites · {s.highlightNodes.slice(0, 3).join(" · ")}
                    </span>
                  ) : null}
                </div>
              </div>
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
}
